<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { bankNameForClearing, normalizeBankAccount } from '@/utils/bankAccount'
import {
  bankAccountIssue,
  isPlausibleBankAccount,
  isValidAccountNumber,
  isValidClearingNumber,
} from '@/utils/validation'

// The bank-transfer leg of FPayoutMethodFields. Both fields are OPTIONAL — empty passes
// (the profile page can be submitted untouched as a no-op, mirroring the legacy skip
// semantics); a non-empty value must match the format.
const clearingNumber = defineModel<string>('clearingNumber', { required: true })
const accountNumber = defineModel<string>('accountNumber', { required: true })

// Separators are stripped as they arrive ("8000-2" → "80002", requirement #1) so every rule
// sees digits. The fields are text + inputmode="numeric", not type="number": a number input
// reports a pasted "8000-2" as EMPTY (badInput) — the value never reaches the model, no rule
// can fire, and in the profile dialog the empty pair would read as "cleared" and delete the
// stored account. inputmode still raises the numeric keyboard on mobile.
watch(clearingNumber, (value) => {
  const digits = value.replace(/\D/g, '')
  if (digits !== value) clearingNumber.value = digits
})
watch(accountNumber, (value) => {
  const digits = value.replace(/\D/g, '')
  if (digits !== value) accountNumber.value = digits
})

const { t } = useI18n()

function clearingRule(value: string) {
  return value === '' || isValidClearingNumber(value) || t('common.number_of_digits', ['4-5'])
}

function accountRule(value: string) {
  return value === '' || isValidAccountNumber(value) || t('common.number_of_digits', ['5-15'])
}

// The bank follows from the clearing number, so it is shown back rather than asked for: no bank
// list, no reverse lookup, and no way for the two to disagree (ADA-4345, developer decision
// 18 Aug). Placed directly AFTER the clearing number, where it reads as the answer to what was
// just typed rather than an empty field waiting for input (developer decision 19 Aug).
const bankName = computed(() => bankNameForClearing(clearingNumber.value))

// The registry line (ADA-4344 decisions #1/#7): what Bankgirot's specification proves
// impossible BLOCKS — the api rejects it with a 422 anyway, so surfacing it here is the only
// version the debtor can act on. Shown lazily like the warning below (both fields complete and
// shaped, never while typing), and the texts end with the contact-us path in case the registry
// is wrong about a real account.
const blockingMessage = computed(() => {
  if (!isValidClearingNumber(clearingNumber.value) || !isValidAccountNumber(accountNumber.value)) return undefined
  const issue = bankAccountIssue(clearingNumber.value, accountNumber.value)
  if (issue === 'unknown_bank') return t('common.unknown_bank_for_clearing')
  if (issue === 'invalid_clearing') return t('common.clearing_five_digits_invalid')
  if (issue === 'invalid_account') return t('common.invalid_bank_account_number')
  return undefined
})

// Check digit and per-bank length rules for what remains after the blocking line: the TYPE 2
// class, where the specification does not guarantee a verifiable check digit. WARNS, never
// blocks — real accounts fail this, and a debtor with an unusual but real account must not be
// locked out of their own money. Only shown once both fields are filled in and shaped
// correctly, so it does not fire while typing.
const showAccountWarning = computed(
  () =>
    blockingMessage.value === undefined &&
    isValidClearingNumber(clearingNumber.value) &&
    isValidAccountNumber(accountNumber.value) &&
    !isPlausibleBankAccount(clearingNumber.value, accountNumber.value)
)

// The stored value is the normalized value (ADA-4344 decision #5): separators dropped and
// overlong zero-padding stripped, so what the debtor sees confirmed is exactly what is paid.
function normalizeFields() {
  const normalized = normalizeBankAccount(clearingNumber.value, accountNumber.value)
  clearingNumber.value = normalized.clearingNumber
  accountNumber.value = normalized.accountNumber
}
</script>

<template>
  <div class="text-body-medium px-6 pt-2">{{ t('common.payout_details_text') }}</div>

  <div class="px-6 pt-6">
    <v-text-field
      v-model="clearingNumber"
      :hint="t('common.number_of_digits', ['4-5'])"
      :label="t('common.clearing_number')"
      :rules="[clearingRule]"
      inputmode="numeric"
      class="mb-4"
      @blur="normalizeFields"
    />

    <v-text-field
      :label="t('common.bank')"
      :model-value="bankName"
      :placeholder="t('common.bank_from_clearing_number')"
      persistent-placeholder
      readonly
      class="mb-4"
    />

    <v-text-field
      v-model="accountNumber"
      :hint="t('common.number_of_digits', ['5-15'])"
      :label="t('common.account_number')"
      :rules="[accountRule]"
      inputmode="numeric"
      @blur="normalizeFields"
    />

    <v-alert
      v-if="blockingMessage"
      :text="blockingMessage"
      type="error"
      density="compact"
      variant="tonal"
      class="mt-4"
    />

    <v-alert
      v-if="showAccountWarning"
      :text="t('common.bank_account_check_digit_warning')"
      type="warning"
      density="compact"
      variant="tonal"
      class="mt-4"
    />
  </div>
</template>
